import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../../Ui_Components/PageHeader";
import { ReactComponent as VegSvg } from "../../../../assects/svg/Recipe/Veg.svg";
import { ReactComponent as NonVegSvg } from "../../../../assects/svg/Recipe/Non_Veg.svg";
import { ReactComponent as NoImageIcon } from "../../../../assects/svg/food_lib/no-image-view.svg";
import { ToastContainer, useToast } from "../../../Ui_Components/ToastMessage";
import { Button } from "../../../Ui_Components/Button";
import IngredientReasonModal, {
  ReasonModalMode,
} from "../../Ingredient/IngredientReasonModal";
import type { IngredientActivityAction } from "../../Ingredient/types";
import type { DishDetail } from "../types";
import {
  archiveOrRestoreDish,
  getDishDetails,
} from "../../../../services/dishes";
import { mapApiToDishDetail } from "../dishApiMapper";
import { useAuth } from "../../../../context/auth/AuthContext";

// Colors mirror the ingredient view so both pages read as one design system.
const STAT_COLORS = {
  cal: "#D946A6",
  carb: "#F59E0B",
  netCarb: "#F59E0B",
  fats: "#EC4899",
  apro: "#22B5D8",
  grossPro: "#1F1F1F",
  gl: "#22C55E",
  fiber: "#8B5CF6",
  cpf: "#2563EB",
};

// Micro-nutrient row config, keyed by the backend snake_case field name that
// appears in dish.micronutrients. Mirrors the ingredient view exactly.
type MicroField = { key: string; label: string; unit: "mcg" | "mg" };
const MICRO_ROWS: MicroField[] = [
  { key: "vitamin_a", label: "Vitamin A", unit: "mcg" },
  { key: "vitamin_d", label: "Vitamin D", unit: "mcg" },
  { key: "vitamin_e", label: "Vitamin E", unit: "mcg" },
  { key: "vitamin_k", label: "Vitamin K", unit: "mcg" },
  { key: "vitamin_c", label: "Vitamin C", unit: "mcg" },
  { key: "thiamine_b1", label: "Thiamine B1", unit: "mg" },
  { key: "riboflavin_b2", label: "Riboflavin B2", unit: "mg" },
  { key: "niacin_b3", label: "Niacin B3", unit: "mg" },
  { key: "pantothenic_acid_b5", label: "Panto. B5", unit: "mg" },
  { key: "vitamin_b6", label: "Vitamin B6", unit: "mg" },
  { key: "biotin_b7", label: "Biotin B7", unit: "mg" },
  { key: "folate_b9", label: "Folate B9", unit: "mg" },
  { key: "vitamin_b12", label: "Vitamin B12", unit: "mcg" },
  { key: "choline", label: "Choline", unit: "mg" },
  { key: "calcium", label: "Calcium", unit: "mg" },
  { key: "phosphorus", label: "Phosphorus", unit: "mg" },
  { key: "magnesium", label: "Magnesium", unit: "mg" },
  { key: "sodium", label: "Sodium", unit: "mg" },
  { key: "potassium", label: "Potassium", unit: "mg" },
  { key: "chloride", label: "Chloride", unit: "mg" },
  { key: "iron", label: "Iron", unit: "mg" },
  { key: "zinc", label: "Zinc", unit: "mg" },
  { key: "copper", label: "Copper", unit: "mg" },
  { key: "manganese", label: "Manganese", unit: "mg" },
  { key: "selenium", label: "Selenium", unit: "mcg" },
  { key: "iodine", label: "Iodine", unit: "mcg" },
  { key: "chromium", label: "Chromium", unit: "mcg" },
  { key: "molybdenum", label: "Molybdenum", unit: "mcg" },
  { key: "fluoride", label: "Fluoride", unit: "mg" },
];

// Trim trailing zeros ("150.00" → "150", "0.05" → "0.05").
const formatMicroValue = (raw: string): string => {
  if (!raw) return "—";
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return String(parseFloat(n.toFixed(6)));
};

const InfoField: React.FC<{
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
}> = ({ label, value, valueClassName }) => (
  <div className="flex flex-col gap-1">
    <span className="text-[10px] 3xl:text-xs font-inter uppercase tracking-wider text-[#8E8E93]">
      {label}
    </span>
    <span
      className={`text-sm 3xl:text-base font-inter text-[#1F1F1F] ${
        valueClassName ?? ""
      }`}
    >
      {value}
    </span>
  </div>
);

const Stat: React.FC<{ label: string; value: string; color: string }> = ({
  label,
  value,
  color,
}) => (
  <div className="flex items-baseline gap-1.5">
    <span className="text-sm 3xl:text-base font-inter text-[#1F1F1F]">
      {label} -
    </span>
    <span
      className="text-sm 3xl:text-base font-semibold font-inter"
      style={{ color }}
    >
      {value}
    </span>
  </div>
);

const ViewDishPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    toasts,
    removeToast,
    error: showError,
    success: showSuccess,
  } = useToast();
  const { user } = useAuth();
  const nutritionistId = user?.nutrist_id;
  const [reasonMode, setReasonMode] = useState<ReasonModalMode | null>(null);

  // Server-driven detail. Fetched on mount from GET_DISH_DETAILS; the row
  // stays null until the response lands so the page can render loading /
  // error states in place of the layout.
  const [dish, setDish] = useState<DishDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setErrorMsg("Missing dish id");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErrorMsg(null);
    (async () => {
      try {
        const api = await getDishDetails(id);
        if (cancelled) return;
        const isSuccess =
          api?.success === true ||
          api?.success === "1" ||
          api?.success === 1 ||
          api?.success === "true";
        if (!isSuccess || !api?.data) {
          setErrorMsg(api?.message || "Failed to load dish");
          setLoading(false);
          return;
        }
        setDish(mapApiToDishDetail(api.data));
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        setErrorMsg(err instanceof Error ? err.message : "Failed to load dish");
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  /* Pick up a deferred success toast from AddDishPage after an edit save.
     Ref guards against React 18 StrictMode's double-invoke; nav state is
     cleared so the toast doesn't re-fire on refresh or back-navigation. */
  const toastFiredRef = useRef(false);
  useEffect(() => {
    if (toastFiredRef.current) return;
    const state = location.state as
      | { showSuccessToast?: boolean; toastMessage?: string }
      | null;
    if (state?.showSuccessToast) {
      toastFiredRef.current = true;
      showSuccess(state.toastMessage || "Success");
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || errorMsg || !dish) {
    return (
      <div className="h-screen flex flex-col bg-[#F7F7FA] overflow-hidden">
        <PageHeader
          title="View dish"
          breadcrumb="Dish > View dish"
          breadcrumbLinks={["/food-library/dish"]}
        />
        <div className="flex-1 flex items-center justify-center">
          <p
            className={`font-inter ${
              errorMsg ? "text-red-500" : "text-gray-500"
            }`}
          >
            {loading ? "Loading dish..." : errorMsg || "Dish not found"}
          </p>
        </div>
        <ToastContainer toasts={toasts} onClose={removeToast} />
      </div>
    );
  }

  const isArchived = dish.status === "Archived";

  const rightContent = (
    <div className="flex items-center gap-3">
      {isArchived ? (
        <>
          <Button
            variant="secondary"
            size="md"
            onClick={() => setReasonMode("restore")}
          >
            Restore
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={() =>
              navigate(`/food-library/dish/${dish.id}/edit`, {
                state: { dish },
              })
            }
          >
            Edit
          </Button>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={() => setReasonMode("archive")}
            className="px-5 py-2 border bg-white border-[#F87171] text-[#F87171] hover:bg-[#FEF2F2] transition-colors text-sm 3xl:text-[16px] font-medium font-inter rounded-[6px]"
          >
            Archive
          </button>
          <Button
            variant="primary"
            size="md"
            onClick={() =>
              navigate(`/food-library/dish/${dish.id}/edit`, {
                state: { dish },
              })
            }
          >
            Edit
          </Button>
        </>
      )}
    </div>
  );

  return (
    <div className="h-screen flex flex-col bg-[#F7F7FA] overflow-hidden">
      <PageHeader
        title="View dish"
        breadcrumb="Dish > View dish"
        breadcrumbLinks={["/food-library/dish"]}
        rightContent={rightContent}
      />

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-8 pb-8 custom-scrollbar-overlay">
        {/* Top row: image (spans info + tags) + info card + tags card */}
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] lg:grid-rows-[auto_auto] gap-4">
          <div className="rounded-md overflow-hidden bg-[#E5DFC8] min-h-[200px] lg:min-h-[220px] h-full lg:row-span-2">
            {dish.imageUrl ? (
              <img
                src={dish.imageUrl}
                alt={dish.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <NoImageIcon
                className="w-full h-full"
                preserveAspectRatio="none"
              />
            )}
          </div>

          {/* Info card */}
          <div className="bg-white rounded-md border border-[#F0F0F0] flex flex-col">
            <div className="px-5 py-4 flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="text-base sm:text-lg 3xl:text-[20px] font-semibold font-inter text-[#1F1F1F]">
                  {dish.name}
                </span>
                {dish.dietType === "veg" && <VegSvg className="w-4 h-4" />}
                {dish.dietType === "nonveg" && (
                  <NonVegSvg className="w-4 h-4" />
                )}
              </div>
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs 3xl:text-[14px] font-semibold font-inter ${
                  dish.status === "Active"
                    ? "bg-[#EBF7F4] text-[#0DB5B5]"
                    : "bg-gray-100 text-gray-500"
                }`}
              >
                {dish.status}
              </span>
            </div>

            <div className="px-5 pb-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <InfoField label="BASE QUANTITY" value={dish.baseQuantity} />
              <InfoField label="CREATED BY" value={dish.createdBy} />
              <InfoField
                label="CATEGORY"
                value={dish.category?.trim() ? dish.category : "—"}
              />
            </div>
          </div>

          {/* Tags card */}
          <div className="bg-white rounded-md border border-[#F0F0F0] px-5 py-4 flex flex-col gap-2">
            <span className="text-[10px] 3xl:text-xs font-inter uppercase tracking-wider text-[#8E8E93]">
              TAGS
            </span>
            {dish.tags.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {dish.tags.map((tag, idx) => (
                  <span
                    key={`${tag}-${idx}`}
                    className="inline-flex items-center px-3 py-1 rounded-full bg-[#F2F2F2] text-[#1F1F1F] text-xs 3xl:text-sm font-inter"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-sm 3xl:text-base font-inter text-[#1F1F1F]">
                —
              </span>
            )}
          </div>
        </div>

        {/* Macro stats card */}
        <div className="mt-4 bg-white rounded-md border border-[#F0F0F0] px-5 py-4">
          <h3 className="text-base sm:text-lg 3xl:text-[20px] font-semibold font-inter text-[#1F1F1F] mb-3">
            Macro
          </h3>
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
            <Stat
              label="Cal"
              value={String(dish.calories)}
              color={STAT_COLORS.cal}
            />
            <Stat
              label="Carb"
              value={`${dish.carbs}g`}
              color={STAT_COLORS.carb}
            />
            <Stat
              label="Net Carb"
              value={`${dish.netCarbs}g`}
              color={STAT_COLORS.netCarb}
            />
            <Stat
              label="Fats"
              value={`${dish.fats}g`}
              color={STAT_COLORS.fats}
            />
            <Stat
              label="A.pro"
              value={`${dish.adjProtein}g`}
              color={STAT_COLORS.apro}
            />
            <Stat
              label="Gross pro"
              value={`${dish.grossProtein}g`}
              color={STAT_COLORS.grossPro}
            />
            <Stat label="GL" value={`${dish.gl}`} color={STAT_COLORS.gl} />
            <Stat
              label="Fiber"
              value={`${dish.fiber ?? 0}g`}
              color={STAT_COLORS.fiber}
            />
            <Stat label="C:P:F" value={dish.cpfRatio} color={STAT_COLORS.cpf} />
          </div>
        </div>

        {/* Micro nutrients card */}
        <div className="mt-4 bg-white rounded-md border border-[#F0F0F0] px-5 py-4">
          <h3 className="text-base sm:text-lg 3xl:text-[20px] font-semibold font-inter text-[#1F1F1F] mb-4">
            Micro
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-4">
            {MICRO_ROWS.map((row) => {
              const raw = dish.micronutrients?.[row.key] ?? "";
              const display = formatMicroValue(raw);
              return (
                <div key={row.key} className="flex flex-col gap-1">
                  <span className="text-xs 3xl:text-sm font-inter text-[#8E8E93]">
                    {row.label} -
                  </span>
                  <span className="text-sm 3xl:text-base font-semibold font-inter text-[#1F1F1F]">
                    {display === "—" ? display : `${display} ${row.unit}`}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Middle row: unit conversions + cooking fat + activity */}
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-md border border-[#F0F0F0] p-5">
            <h3 className="text-base sm:text-lg 3xl:text-[20px] font-semibold font-inter text-[#1F1F1F] mb-4">
              Unit conversions
            </h3>
            <div className="flex flex-col gap-3">
              {dish.unitConversions.map((row, idx) => (
                <div
                  key={`${row.unit}-${idx}`}
                  className="flex items-center justify-between"
                >
                  <span className="text-sm 3xl:text-base font-inter text-[#1F1F1F]">
                    {row.unit}
                  </span>
                  <span className="text-sm 3xl:text-base font-inter text-[#6B7280]">
                    {row.grams}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {dish.cookingFatBands.length > 0 && (
            <div className="bg-white rounded-md border border-[#F0F0F0] p-5">
              <h3 className="text-base sm:text-lg 3xl:text-[20px] font-semibold font-inter text-[#1F1F1F] mb-4">
                Cooking fat
              </h3>
              <div className="grid grid-cols-3 gap-x-3 gap-y-3">
                <span className="text-xs 3xl:text-sm font-inter text-[#8E8E93]">
                  Min qty (g)
                </span>
                <span className="text-xs 3xl:text-sm font-inter text-[#8E8E93]">
                  Max qty (g)
                </span>
                <span className="text-xs 3xl:text-sm font-inter text-[#8E8E93]">
                  Fat (g)
                </span>
                {dish.cookingFatBands.map((band, idx) => (
                  <React.Fragment key={`band-${idx}`}>
                    <span className="text-sm 3xl:text-base font-inter text-[#1F1F1F]">
                      {band.minQty}
                    </span>
                    <span className="text-sm 3xl:text-base font-inter text-[#1F1F1F]">
                      {band.maxQty}
                    </span>
                    <span className="text-sm 3xl:text-base font-inter text-[#1F1F1F]">
                      {band.fat}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* Activity — takes 2/3 of the row when Cooking fat is hidden so
              the layout matches the ingredient view's two-card pattern. */}
          <div
            className={`bg-white rounded-md border border-[#F0F0F0] p-5 ${
              dish.cookingFatBands.length === 0 ? "lg:col-span-2" : ""
            }`}
          >
            <h3 className="text-base sm:text-lg 3xl:text-[20px] font-semibold font-inter text-[#1F1F1F] mb-4">
              Activity
            </h3>
            <div className="flex flex-col">
              {dish.activity.map((entry, idx) => {
                const isFirst = idx === 0;
                const isLast = idx === dish.activity.length - 1;
                const actionLabelMap: Record<IngredientActivityAction, string> =
                  {
                    created: "Created",
                    added: "Added",
                    edited: "Edited",
                    archived: "Archived",
                    restored: "Restored",
                  };
                const actionLabel = actionLabelMap[entry.action];
                const showRemark =
                  (entry.action === "archived" ||
                    entry.action === "restored") &&
                  !!entry.remark;
                return (
                  <div key={entry.id} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        className="mt-1 shrink-0 block"
                      >
                        <circle
                          cx="8"
                          cy="8"
                          r="7"
                          fill="white"
                          stroke={isFirst ? "#0DB5B5" : "#D1D5DB"}
                          strokeWidth="2"
                        />
                        <circle
                          cx="8"
                          cy="8"
                          r="3"
                          fill={isFirst ? "#0DB5B5" : "#D1D5DB"}
                        />
                      </svg>
                      {!isLast && (
                        <span className="flex-1 w-0 border-l border-dashed border-[#D1D5DB] my-1" />
                      )}
                    </div>
                    <div className={`pb-4 ${isLast ? "pb-0" : ""}`}>
                      <p className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
                        {actionLabel} by {entry.actor}
                      </p>
                      <p className="text-xs 3xl:text-sm font-inter text-[#8E8E93] mt-0.5">
                        {entry.date}
                        {entry.time ? ` · ${entry.time}` : ""}
                        {showRemark ? ` · ${entry.remark}` : ""}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Recipe instructions */}
        <div className="mt-4 bg-white rounded-md border border-[#F0F0F0] p-5">
          <h3 className="text-base sm:text-lg 3xl:text-[20px] font-semibold font-inter text-[#1F1F1F] mb-3">
            Recipe instruction
          </h3>
          {dish.recipeInstructions.length > 0 ? (
            <ol className="list-decimal pl-5 flex flex-col gap-1.5">
              {dish.recipeInstructions.map((step, idx) => (
                <li
                  key={idx}
                  className="text-sm 3xl:text-base font-inter text-[#1F1F1F]"
                >
                  {step}
                </li>
              ))}
            </ol>
          ) : (
            <span className="text-sm 3xl:text-base font-inter text-[#6B7280]">
              No recipe instructions added.
            </span>
          )}
        </div>

        {/* Ingredient list */}
        <div className="mt-4 bg-white rounded-md border border-[#F0F0F0] p-5">
          <h3 className="text-base sm:text-lg 3xl:text-[20px] font-semibold font-inter text-[#1F1F1F] mb-4">
            Ingredient list
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] table-fixed">
              <thead>
                <tr className="border-b border-[#111C2D1A]">
                  <th className="text-left py-3 px-2 text-xs 3xl:text-sm font-inter font-normal text-[#8E8E93] w-[22%]">
                    Ingredient
                  </th>
                  <th className="text-left py-3 px-2 text-xs 3xl:text-sm font-inter font-normal text-[#8E8E93] w-[9%]">
                    Quantity
                  </th>
                  <th className="text-left py-3 px-2 text-xs 3xl:text-sm font-inter font-normal text-[#8E8E93] w-[9%]">
                    Unit
                  </th>
                  <th className="text-left py-3 px-2 text-xs 3xl:text-sm font-inter font-normal text-[#8E8E93] w-[8%]">
                    Cal
                  </th>
                  <th className="text-left py-3 px-2 text-xs 3xl:text-sm font-inter font-normal text-[#8E8E93] w-[7%]">
                    Carb
                  </th>
                  <th className="text-left py-3 px-2 text-xs 3xl:text-sm font-inter font-normal text-[#8E8E93] w-[7%]">
                    Fats
                  </th>
                  <th className="text-left py-3 px-2 text-xs 3xl:text-sm font-inter font-normal text-[#8E8E93] w-[8%]">
                    A.Prot
                  </th>
                  <th className="text-left py-3 px-2 text-xs 3xl:text-sm font-inter font-normal text-[#8E8E93] w-[7%]">
                    GL
                  </th>
                  <th className="text-left py-3 px-2 text-xs 3xl:text-sm font-inter font-normal text-[#8E8E93] w-[10%] whitespace-nowrap">
                    C:P:F
                  </th>
                </tr>
              </thead>
              <tbody>
                {dish.ingredientList.map((row, idx) => {
                  const isLast = idx === dish.ingredientList.length - 1;
                  const borderCls = isLast
                    ? ""
                    : "border-b border-[#111C2D1A]";
                  return (
                    <tr key={row.id}>
                      <td className={`py-3 px-2 ${borderCls}`}>
                        <div className="flex items-center gap-2">
                          {row.imageUrl ? (
                            <img
                              src={row.imageUrl}
                              alt={row.name}
                              className="w-8 h-8 rounded-[4px] object-cover flex-shrink-0"
                            />
                          ) : (
                            <NoImageIcon className="w-8 h-8 rounded-[4px] flex-shrink-0" />
                          )}
                          <span className="text-sm 3xl:text-base font-inter text-[#1F1F1F]">
                            {row.name}
                          </span>
                        </div>
                      </td>
                      <td
                        className={`py-3 px-2 text-sm 3xl:text-base font-inter text-[#1F1F1F] ${borderCls}`}
                      >
                        {row.quantity}
                      </td>
                      <td
                        className={`py-3 px-2 text-sm 3xl:text-base font-inter text-[#1F1F1F] ${borderCls}`}
                      >
                        {row.unit}
                      </td>
                      <td
                        className={`py-3 px-2 text-sm 3xl:text-base font-inter text-[#1F1F1F] ${borderCls}`}
                      >
                        {row.cal}
                      </td>
                      <td
                        className={`py-3 px-2 text-sm 3xl:text-base font-inter text-[#1F1F1F] ${borderCls}`}
                      >
                        {row.carb}
                      </td>
                      <td
                        className={`py-3 px-2 text-sm 3xl:text-base font-inter text-[#1F1F1F] ${borderCls}`}
                      >
                        {row.fats}
                      </td>
                      <td
                        className={`py-3 px-2 text-sm 3xl:text-base font-inter text-[#1F1F1F] ${borderCls}`}
                      >
                        {row.aProt}
                      </td>
                      <td
                        className={`py-3 px-2 text-sm 3xl:text-base font-inter text-[#1F1F1F] ${borderCls}`}
                      >
                        {row.gl}
                      </td>
                      <td
                        className={`py-3 px-2 text-sm 3xl:text-base font-inter text-[#1F1F1F] whitespace-nowrap ${borderCls}`}
                      >
                        {row.cpf}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <IngredientReasonModal
        open={reasonMode !== null}
        mode={reasonMode ?? "archive"}
        itemLabel="dish"
        ingredientName={dish.name}
        onClose={() => setReasonMode(null)}
        onConfirm={async (reason) => {
          // On success navigate back to the list with a deferred toast — the
          // dish's status has changed so this view is no longer the right
          // landing page (list tab handles both Active and Archived).
          const mode = reasonMode;
          const isRestore = mode === "restore";
          if (!nutritionistId) {
            showError("You are not signed in", "Please sign in and retry.");
            return;
          }
          try {
            const api = await archiveOrRestoreDish({
              dish_id: dish.id,
              is_hide: !isRestore,
              reason,
              nutritionist_id: nutritionistId,
            });
            const isSuccess =
              api?.success === true ||
              api?.success === "1" ||
              api?.success === 1 ||
              api?.success === "true";
            if (!isSuccess) {
              throw new Error(
                api?.message ||
                  (isRestore
                    ? "Failed to restore dish"
                    : "Failed to archive dish"),
              );
            }
            setReasonMode(null);
            navigate("/food-library/dish", {
              state: {
                showSuccessToast: true,
                toastMessage:
                  api?.message ||
                  (isRestore
                    ? "Dish restored successfully"
                    : "Dish archived successfully"),
              },
            });
          } catch (err) {
            showError(
              isRestore ? "Failed to restore dish" : "Failed to archive dish",
              err instanceof Error ? err.message : undefined,
            );
          }
        }}
      />

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
};

export default ViewDishPage;
